import React, {
  useRef,
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
import {
  GridLayout,
  Layout,
  LayoutItem,
  verticalCompactor,
} from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import "./SN_SYS_DSH_REP_001.css";
import SearchPanel from "@widgets/SearchPanel";
import { fnGetDataSet, fnSave } from "@shared/api/restApi";
import { getInitFormData } from "@shared/utils/CommonUtils";
import { showDialog } from "@widgets/CommonDialog";
import { global } from "@shared/libs/Global";
import { menuInfo } from "@shared/type/CommonType";
import DashboardDetailPanel, {
  DETAIL_PANEL_DEFAULT_BOUNDS,
  DETAIL_PANEL_MIN_WIDTH,
  DetailPanelBounds,
} from "../../../Dashboard/Widget/DashboardDetailPanel";
import {
  icoDlv,
  icoPrd01,
  icoPur01,
  icoQms01,
  icoRankSales,
  icoSal01,
  snSysWidget001,
  snSysWidget002,
  snSysWidget003,
  snSysWidget004,
  snSysWidget014,
} from "../../../Dashboard/Widget/icons";

declare const require: {
  context(
    path: string,
    deep?: boolean,
    filter?: RegExp
  ): {
    keys: () => string[];
    <T>(id: string): T;
  };
};

interface Props {
  Menuprops: menuInfo;
}

type WidgetModule = {
  default: React.ComponentType<any>;
};

type DashboardWidget = {
  cd_widget: string;
  nm_widget: string;
  dc_widget?: string;
  nm_file: string;
  cd_dashboard?: string;
  // DB 저장 컬럼은 qt_x/qt_y/qt_w/qt_h이고, GridLayout에는 x/y/w/h로 변환해서 전달한다.
  qt_x: number;
  qt_y: number;
  qt_w: number;
  qt_h: number;
  qt_w_min?: number | null;
  qt_h_min?: number | null;
  qt_w_max?: number | null;
  qt_h_max?: number | null;
  yn_visible?: string;
  // sys_mwidget.yn_detail. Y면 상세보기 버튼을 표시하고 nm_file + "_01" 파일을 팝업으로 띄운다.
  yn_detail?: string;
  isNew?: boolean;
};

type AvailableWidget = {
  cd_widget: string;
  nm_widget: string;
  dc_widget?: string;
  nm_file: string;
  qt_w_default?: number | null;
  qt_h_default?: number | null;
  qt_w_min?: number | null;
  qt_h_min?: number | null;
  qt_w_max?: number | null;
  qt_h_max?: number | null;
};

type DashboardDetailState = {
  open: boolean;
  widget?: DashboardWidget;
};

type WidgetRect = {
  // Grid 충돌 계산용 내부 좌표. 단위는 pixel이 아니라 grid cell이다.
  x: number;
  y: number;
  w: number;
  h: number;
};

const DEFAULT_DASHBOARD = "MAIN";
// 크기 미세 조정을 위해 12칸/86px 그리드를 가로세로 절반 단위로 쪼갰다(저장값은 2배로 변환됨).
// 높이 h칸 = h*ROW_HEIGHT + (h-1)*GAP 이라 ROW_HEIGHT 39면 기존 1칸(94px)이 정확히 2칸이 된다.
const COL_COUNT = 24;
const ROW_HEIGHT = 39;
const GAP = 8;
const DEFAULT_WIDGET_W = 6;
const DEFAULT_WIDGET_H = 4;
const SAVE_DEBOUNCE_MS = 800;
const GRID_MARGIN: [number, number] = [GAP, GAP];
const GRID_CONTAINER_PADDING: [number, number] = [0, 0];

const widgetContext = require.context(
  "../../../Dashboard/Widget",
  false,
  /\.tsx$/
);

const widgetMap = widgetContext
  .keys()
  .reduce((acc: Record<string, React.ComponentType<any>>, path: string) => {
    const fileName = path.replace("./", "").replace(".tsx", "");
    acc[fileName] = (widgetContext<WidgetModule>(path) as WidgetModule).default;
    return acc;
  }, {});

const widgetPreviewIcons: Record<string, string> = {
  sn_sys_widget_001: snSysWidget001,
  sn_sys_widget_002: snSysWidget002,
  sn_sys_widget_003: snSysWidget003,
  sn_sys_widget_004: snSysWidget004,
  sn_sys_widget_005: icoSal01,
  sn_sys_widget_006: icoPur01,
  sn_sys_widget_007: icoDlv,
  sn_sys_widget_008: icoQms01,
  sn_sys_widget_009: icoPrd01,
  sn_sys_widget_010: icoRankSales,
  sn_sys_widget_012: icoPur01,
  sn_sys_widget_013: icoPrd01,
  sn_sys_widget_014: snSysWidget014,
};

const toNumber = (value: unknown, fallback: number) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : fallback;
};

// 조회 결과 row를 화면에서 쓰는 DashboardWidget 형태로 정규화한다.
const normalizeWidget = (row: any): DashboardWidget => ({
  cd_widget: row?.cd_widget || row?.nm_file || "",
  nm_widget: row?.nm_widget || row?.cd_widget || row?.nm_file || "",
  dc_widget: row?.dc_widget || "",
  nm_file: row?.nm_file || "",
  cd_dashboard: row?.cd_dashboard || DEFAULT_DASHBOARD,
  qt_x: Math.max(0, toNumber(row?.qt_x ?? row?.x, 0)),
  qt_y: Math.max(0, toNumber(row?.qt_y ?? row?.y, 0)),
  qt_w: Math.max(1, toNumber(row?.qt_w ?? row?.w, DEFAULT_WIDGET_W)),
  qt_h: Math.max(1, toNumber(row?.qt_h ?? row?.h, DEFAULT_WIDGET_H)),
  qt_w_min: row?.qt_w_min ?? row?.minW ?? null,
  qt_h_min: row?.qt_h_min ?? row?.minH ?? null,
  qt_w_max: row?.qt_w_max ?? row?.maxW ?? null,
  qt_h_max: row?.qt_h_max ?? row?.maxH ?? null,
  yn_visible: row?.yn_visible || "Y",
  yn_detail: row?.yn_detail || "N",
  isNew: row?.is_new === "Y" || row?.isNew === true || row?.is_new === true,
});

// Convert hidden dashboard state into an add-panel item.
const toAvailableWidget = (widget: DashboardWidget): AvailableWidget => ({
  cd_widget: widget.cd_widget,
  nm_widget: widget.nm_widget,
  dc_widget: widget.dc_widget,
  nm_file: widget.nm_file,
  qt_w_default: widget.qt_w,
  qt_h_default: widget.qt_h,
  qt_w_min: widget.qt_w_min,
  qt_h_min: widget.qt_h_min,
  qt_w_max: widget.qt_w_max,
  qt_h_max: widget.qt_h_max,
});

const getWidgetRect = (widget: DashboardWidget): WidgetRect => ({
  x: widget.qt_x,
  y: widget.qt_y,
  w: widget.qt_w,
  h: widget.qt_h,
});

// 두 위젯의 grid 영역이 서로 겹치는지 판단한다.
const collides = (a: WidgetRect, b: WidgetRect) => {
  if (a.x + a.w <= b.x) return false;
  if (b.x + b.w <= a.x) return false;
  if (a.y + a.h <= b.y) return false;
  if (b.y + b.h <= a.y) return false;
  return true;
};

// DB/쿼리에서 넘어온 좌표와 크기를 min/max 및 12컬럼 범위 안으로 보정한다.
const normalizeRect = (widget: DashboardWidget): WidgetRect => {
  const minW = Math.max(1, toNumber(widget.qt_w_min, 1));
  const minH = Math.max(1, toNumber(widget.qt_h_min, 1));
  const maxW = Math.max(minW, toNumber(widget.qt_w_max, COL_COUNT));
  const maxH = Math.max(minH, toNumber(widget.qt_h_max, 999));
  const w = Math.min(COL_COUNT, maxW, Math.max(minW, widget.qt_w));
  const h = Math.min(maxH, Math.max(minH, widget.qt_h));

  return {
    x: Math.max(0, Math.min(widget.qt_x, COL_COUNT - w)),
    y: Math.max(0, widget.qt_y),
    w,
    h,
  };
};

// 숨김/추가/조회 후 빈 공간을 줄이기 위해 visible 위젯만 위쪽으로 compact한다.
const compactWidgets = (widgets: DashboardWidget[]) => {
  const visibleWidgets = widgets
    .filter((widget) => widget.yn_visible !== "N")
    .sort((a, b) => a.qt_y - b.qt_y || a.qt_x - b.qt_x);
  const placed: WidgetRect[] = [];
  const nextByCode = new Map<string, WidgetRect>();

  visibleWidgets.forEach((widget) => {
    const rect = normalizeRect(widget);

    while (
      rect.y > 0 &&
      !placed.some((placedRect) =>
        collides({ ...rect, y: rect.y - 1 }, placedRect)
      )
    ) {
      rect.y -= 1;
    }

    while (placed.some((placedRect) => collides(rect, placedRect))) {
      rect.y += 1;
    }

    placed.push({ ...rect });
    nextByCode.set(widget.cd_widget, rect);
  });

  return widgets.map((widget) => {
    const rect = nextByCode.get(widget.cd_widget);
    if (!rect) return widget;

    return {
      ...widget,
      qt_x: rect.x,
      qt_y: rect.y,
      qt_w: rect.w,
      qt_h: rect.h,
    };
  });
};

// 신규 위젯을 추가할 때 기존 visible 위젯의 마지막 행 아래에 임시 배치한다.
const getNextPosition = (widgets: DashboardWidget[]) => {
  const visibleWidgets = widgets.filter((widget) => widget.yn_visible !== "N");
  if (!visibleWidgets.length) {
    return { x: 0, y: 0, w: DEFAULT_WIDGET_W, h: DEFAULT_WIDGET_H };
  }

  const bottom = visibleWidgets.reduce((max, widget) => {
    return Math.max(max, widget.qt_y + widget.qt_h);
  }, 0);

  return { x: 0, y: bottom, w: DEFAULT_WIDGET_W, h: DEFAULT_WIDGET_H };
};

// DashboardWidget(qt_x/qt_y/qt_w/qt_h)을 react-grid-layout의 LayoutItem(x/y/w/h)으로 변환한다.
const widgetToLayout = (widget: DashboardWidget): LayoutItem => {
  const minW = Math.max(1, toNumber(widget.qt_w_min, 1));
  const minH = Math.max(1, toNumber(widget.qt_h_min, 1));
  const maxW = Math.max(minW, toNumber(widget.qt_w_max, COL_COUNT));
  const maxH = Math.max(minH, toNumber(widget.qt_h_max, 999));
  const w = Math.min(COL_COUNT, maxW, Math.max(minW, widget.qt_w));
  const h = Math.min(maxH, Math.max(minH, widget.qt_h));

  return {
    i: widget.cd_widget,
    x: Math.max(0, Math.min(widget.qt_x, COL_COUNT - w)),
    y: Math.max(0, widget.qt_y),
    w,
    h,
    minW,
    minH,
    maxW: Math.min(maxW, COL_COUNT),
    maxH,
  };
};

// react-grid-layout이 반환한 전체 layout을 기존 DashboardWidget 배열에 다시 반영한다.
const applyLayoutToWidgets = (widgets: DashboardWidget[], layout: Layout) => {
  const layoutByCode = new Map(layout.map((item) => [item.i, item]));

  return widgets.map((widget) => {
    const item = layoutByCode.get(widget.cd_widget);
    if (!item) return widget;

    return {
      ...widget,
      qt_x: item.x,
      qt_y: item.y,
      qt_w: item.w,
      qt_h: item.h,
    };
  });
};

const SN_SYS_DSH_REP_001 = forwardRef(({ Menuprops }: Props, ref) => {
  const canvasRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<any>(null);
  const [formData, setFormData] = useState<any>();
  const [formControlData, setFormControlData] = useState<any[]>([]);
  const [dataSource1, setDataSource1] = useState<DashboardWidget[]>([]);
  const [searchData, setSearchData] = useState<any>({});
  const [refreshKey, setRefreshKey] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);
  const [isDirty, setIsDirty] = useState(false);
  const [isSearched, setIsSearched] = useState(false);
  const [isCatalogOpen, setIsCatalogOpen] = useState(false);
  const [availableWidgets, setAvailableWidgets] = useState<AvailableWidget[]>(
    []
  );
  const [activeDetail, setActiveDetail] = useState<DashboardDetailState>({
    open: false,
  });
  const [detailPanelBounds, setDetailPanelBounds] = useState<DetailPanelBounds>(
    DETAIL_PANEL_DEFAULT_BOUNDS
  );
  const isSearchPanelFoldedRef = useRef(false);
  const isInitialSearchRef = useRef(false);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestWidgetsRef = useRef<DashboardWidget[]>([]);
  const dirtyVersionRef = useRef(0);
  const { userInfo } = global();

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData({
          menuInfo: Menuprops,
          etcInfo: {},
        });
        setFormData(rtn.formData);
        setFormControlData(rtn.formControlData);
      } catch (error) {}
    };
    fetchform();
  }, []);

  useEffect(() => {
    const updateWidth = () => {
      setContainerWidth(canvasRef.current?.clientWidth || 0);
    };

    updateWidth();

    const resizeObserver = new ResizeObserver(updateWidth);
    if (canvasRef.current) {
      resizeObserver.observe(canvasRef.current);
    }

    window.addEventListener("resize", updateWidth);
    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", updateWidth);
    };
  }, []);

  useEffect(() => {
    if (isSearchPanelFoldedRef.current || formControlData.length === 0) return;

    setTimeout(() => {
      searchRef.current?.handleExpand?.(false);
      isSearchPanelFoldedRef.current = true;
    }, 0);
  }, [formControlData]);

  const visibleWidgets = useMemo(() => {
    return dataSource1.filter((widget) => widget.yn_visible !== "N");
  }, [dataSource1]);

  const gridLayout = useMemo(() => {
    return visibleWidgets.map(widgetToLayout);
  }, [visibleWidgets]);

  // debounce로 예약된 자동 저장을 취소한다. 수동 저장/재조회/언마운트 전에 호출한다.
  const clearScheduledSave = () => {
    if (!saveTimerRef.current) return;

    clearTimeout(saveTimerRef.current);
    saveTimerRef.current = null;
  };

  // 기존 저장 API 형식에 맞춰 x/y/w/h를 qt_x/qt_y/qt_w/qt_h로 저장한다.
  const saveDashboardLayout = async (widgets: DashboardWidget[]) => {
    const saveVersion = dirtyVersionRef.current;

    try {
      const formdata = searchRef.current?.getFormdata?.() || {};
      const cdDashboard = formdata.cd_dashboard || DEFAULT_DASHBOARD;

      const SaveData = {
        sys_widget_layout: widgets.map((widget) => ({
          cd_firm: userInfo.cd_firm,
          cd_user: userInfo.cd_user,
          cd_dashboard: widget.cd_dashboard || cdDashboard,
          cd_widget: widget.cd_widget,
          qt_x: widget.qt_x,
          qt_y: widget.qt_y,
          qt_w: widget.qt_w,
          qt_h: widget.qt_h,
          yn_visible: widget.yn_visible || "Y",
          isNew: widget.isNew === true,
        })),
      };

      await fnSave("SN_SYS_DSH_REP_001_SAVE_01", SaveData, undefined, {
        silentSuccess: true,
      });

      if (saveVersion === dirtyVersionRef.current) {
        setIsDirty(false);
      }
      return true;
    } catch (error: any) {
      setIsDirty(true);
      await showDialog("error", error.message);
      return false;
    }
  };

  // drag/resize/hide/add 후 마지막 변경만 저장되도록 자동 저장을 debounce 처리한다.
  const scheduleSave = (widgets: DashboardWidget[]) => {
    latestWidgetsRef.current = widgets;
    clearScheduledSave();
    saveTimerRef.current = setTimeout(() => {
      saveTimerRef.current = null;
      saveDashboardLayout(latestWidgetsRef.current);
    }, SAVE_DEBOUNCE_MS);
  };

  // 화면 state는 즉시 갱신하고 저장은 debounce로 예약한다.
  const updateWidgets = (widgets: DashboardWidget[]) => {
    dirtyVersionRef.current += 1;
    latestWidgetsRef.current = widgets;
    setDataSource1(widgets);
    setIsDirty(true);
    scheduleSave(widgets);
  };

  // 드래그/리사이즈 중 GridLayout의 controlled layout 상태를 계속 동기화한다.
  // 저장은 하지 않고 placeholder/위젯 위치가 남지 않도록 화면 상태만 맞춘다.
  const syncWidgetsLayout = (layout: Layout) => {
    const baseWidgets = latestWidgetsRef.current.length
      ? latestWidgetsRef.current
      : dataSource1;
    const nextDataSource = applyLayoutToWidgets(baseWidgets, layout);

    latestWidgetsRef.current = nextDataSource;
    setDataSource1(nextDataSource);
  };

  // 드래그/리사이즈 종료 시점의 전체 layout을 반영하고 저장을 예약한다.
  const handleLayoutStop = (layout: Layout) => {
    const baseWidgets = latestWidgetsRef.current.length
      ? latestWidgetsRef.current
      : dataSource1;
    const nextDataSource = applyLayoutToWidgets(baseWidgets, layout);
    updateWidgets(nextDataSource);
  };

  // 위젯 숨김은 삭제가 아니라 yn_visible=N 처리 후 남은 visible 위젯을 compact한다.
  const hideWidget = (cdWidget: string) => {
    const nextDataSource = compactWidgets(
      dataSource1.map((widget) =>
        widget.cd_widget === cdWidget ? { ...widget, yn_visible: "N" } : widget
      )
    );
    const hiddenWidget = nextDataSource.find(
      (widget) => widget.cd_widget === cdWidget
    );

    if (hiddenWidget) {
      setAvailableWidgets((prev) => {
        if (prev.some((widget) => widget.cd_widget === cdWidget)) return prev;
        return [...prev, toAvailableWidget(hiddenWidget)];
      });
    }

    updateWidgets(nextDataSource);
  };

  // hidden 위젯은 다시 보이게 하고, 신규 위젯은 마지막 위치에 추가한 뒤 compact한다.
  const addWidget = (availableWidget: AvailableWidget) => {
    const nextPosition = getNextPosition(dataSource1);
    const formdata = searchRef.current?.getFormdata?.() || {};
    const exists = dataSource1.some(
      (widget) => widget.cd_widget === availableWidget.cd_widget
    );
    const nextDataSource = compactWidgets(
      exists
        ? dataSource1.map((widget) =>
            widget.cd_widget === availableWidget.cd_widget
              ? {
                  ...widget,
                  nm_widget: availableWidget.nm_widget,
                  dc_widget: availableWidget.dc_widget,
                  nm_file: availableWidget.nm_file,
                  qt_w: toNumber(availableWidget.qt_w_default, widget.qt_w),
                  qt_h: toNumber(availableWidget.qt_h_default, widget.qt_h),
                  qt_w_min: availableWidget.qt_w_min ?? widget.qt_w_min,
                  qt_h_min: availableWidget.qt_h_min ?? widget.qt_h_min,
                  qt_w_max: availableWidget.qt_w_max ?? widget.qt_w_max,
                  qt_h_max: availableWidget.qt_h_max ?? widget.qt_h_max,
                  yn_visible: "Y",
                  isNew: widget.isNew === true,
                }
              : widget
          )
        : [
            ...dataSource1,
            {
              cd_widget: availableWidget.cd_widget,
              nm_widget: availableWidget.nm_widget,
              dc_widget: availableWidget.dc_widget,
              nm_file: availableWidget.nm_file,
              cd_dashboard: formdata.cd_dashboard || DEFAULT_DASHBOARD,
              qt_x: nextPosition.x,
              qt_y: nextPosition.y,
              qt_w: toNumber(availableWidget.qt_w_default, nextPosition.w),
              qt_h: toNumber(availableWidget.qt_h_default, nextPosition.h),
              qt_w_min: availableWidget.qt_w_min ?? 1,
              qt_h_min: availableWidget.qt_h_min ?? 1,
              qt_w_max: availableWidget.qt_w_max ?? COL_COUNT,
              qt_h_max: availableWidget.qt_h_max ?? null,
              yn_visible: "Y",
              isNew: true,
            },
          ]
    );

    setAvailableWidgets((prev) =>
      prev.filter((widget) => widget.cd_widget !== availableWidget.cd_widget)
    );
    updateWidgets(nextDataSource);
  };

  // 상세보기에서 호출된다. 실제 상세 조회와 그리드는 각 sn_sys_widget_*_01에서 담당한다.
  const openDetailPanel = (widget: DashboardWidget) => {
    setDetailPanelBounds((prev) => ({
      ...prev,
      x: Math.max(16, containerWidth - prev.width - 16),
      y: Math.max(16, prev.y),
      width: Math.min(
        prev.width,
        Math.max(DETAIL_PANEL_MIN_WIDTH, containerWidth - 32)
      ),
    }));

    setActiveDetail({
      open: true,
      widget,
    });
  };

  const onSearch = async () => {
    if (isDirty) {
      const confirm = await showDialog(
        "confirm",
        "Changed dashboard layout exists. Save before search?"
      );

      if (confirm) {
        const saved = await onSave();
        if (!saved) return;
      } else if (confirm === undefined) {
        return;
      } else {
        clearScheduledSave();
      }
    }

    try {
      const formdata = searchRef.current?.getFormdata?.() || {};
      const searchFormdata = {
        ...(formData || {}),
        ...formdata,
      };
      const widgetSearchData = {
        ...searchFormdata,
        dt: formdata.dt || formData?.dt,
        cd_firm: userInfo.cd_firm,
        cd_user: userInfo.cd_user,
        cd_dashboard: searchFormdata.cd_dashboard || DEFAULT_DASHBOARD,
      };
      const param = {
        id: "sn_sys_dsh_rep_001_01",
        params: widgetSearchData,
      };

      const data: any = await fnGetDataSet(param);
      const nextDataSource = compactWidgets(
        (data?.[0] || []).map(normalizeWidget)
      );
      latestWidgetsRef.current = nextDataSource;
      // 조회 결과가 들어오면 이전 화면 변경 예약 저장은 더 이상 유효하지 않다.
      clearScheduledSave();
      setDataSource1(nextDataSource);
      setAvailableWidgets(
        nextDataSource
          .filter((widget) => widget.yn_visible === "N")
          .map(toAvailableWidget)
      );
      setSearchData(widgetSearchData);
      setRefreshKey((prev) => prev + 1);
      setIsSearched(true);
      setIsDirty(false);
    } catch (error: any) {}
  };

  const onInsert = () => {
    const rows = dataSource1
      .filter((widget) => widget.yn_visible === "N")
      .map(toAvailableWidget);

    setAvailableWidgets(rows);
    setIsCatalogOpen(true);
  };

  const onDelete = async () => {
    const visibleWidgets = dataSource1.filter(
      (widget) => widget.yn_visible !== "N"
    );
    if (!visibleWidgets.length) return;

    hideWidget(visibleWidgets[visibleWidgets.length - 1].cd_widget);
  };

  const onSave = async () => {
    // 수동 저장은 예약 저장보다 우선한다.
    clearScheduledSave();
    return saveDashboardLayout(latestWidgetsRef.current);
  };

  useEffect(() => {
    // 화면 이탈 시 대기 중인 자동 저장 타이머를 정리한다.
    return () => clearScheduledSave();
  }, []);

  useEffect(() => {
    if (
      isInitialSearchRef.current ||
      !formData ||
      formControlData.length === 0
    ) {
      return;
    }

    const timer = setTimeout(() => {
      isInitialSearchRef.current = true;
      onSearch();
    }, 0);

    return () => clearTimeout(timer);
  }, [formData, formControlData.length]);

  useImperativeHandle(ref, () => ({
    onSearch,
    onInsert,
    onDelete,
    onSave,
  }));

  return (
    <div className="flex h-full flex-col">
      <div className="mb-1">
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
        ></SearchPanel>
      </div>
      <div className="relative min-h-0 flex-1">
        <div
          ref={canvasRef}
          className={`absolute inset-0 overflow-x-hidden overflow-y-auto border border-gray-200 bg-gray-50 ${
            isCatalogOpen ? "right-72" : "right-0"
          }`}
        >
          {containerWidth > 0 && (
            <GridLayout
              className="dashboard-grid-layout"
              layout={gridLayout}
              width={containerWidth}
              // react-grid-layout v2는 cols/rowHeight/margin을 gridConfig로 받는다.
              gridConfig={{
                cols: COL_COUNT,
                rowHeight: ROW_HEIGHT,
                margin: GRID_MARGIN,
                containerPadding: GRID_CONTAINER_PADDING,
                maxRows: Infinity,
              }}
              // 카드 전체를 드래그 가능하게 하되, Hide 버튼은 이벤트 전파를 막는다.
              dragConfig={{
                enabled: true,
                bounded: false,
                handle: ".dashboard-widget-drag-handle",
                threshold: 3,
              }}
              // 현재는 우하단 리사이즈만 허용한다.
              resizeConfig={{
                enabled: true,
                handles: ["se"],
              }}
              // verticalCompactor가 충돌 위젯을 아래로 밀고 빈 공간을 위쪽으로 정리한다.
              compactor={verticalCompactor}
              onLayoutChange={syncWidgetsLayout}
              onDrag={syncWidgetsLayout}
              onDragStop={handleLayoutStop}
              onResize={syncWidgetsLayout}
              onResizeStop={handleLayoutStop}
            >
              {visibleWidgets.map((widget) => (
                <div key={widget.cd_widget}>
                  <WidgetFrame
                    widget={widget}
                    onHide={hideWidget}
                    onOpenDetail={openDetailPanel}
                  >
                    <WidgetRenderer
                      widget={widget}
                      searchData={searchData}
                      refreshKey={refreshKey}
                    />
                  </WidgetFrame>
                </div>
              ))}
            </GridLayout>
          )}

          {visibleWidgets.length === 0 && (
            <div className="absolute inset-0 flex items-center justify-center text-sm text-gray-500">
              {isSearched
                ? "No dashboard widgets."
                : "Search dashboard widgets."}
            </div>
          )}

          {/* 상세 패널은 dashboard canvas 위에 띄워서 다른 위젯 배치를 바꾸지 않는다. */}
          {activeDetail.open && activeDetail.widget && (
            <DashboardDetailPanel
              title={`${activeDetail.widget.nm_widget} 상세`}
              bounds={detailPanelBounds}
              onBoundsChange={setDetailPanelBounds}
              onClose={() =>
                setActiveDetail((prev) => ({ ...prev, open: false }))
              }
            >
              <WidgetDetailRenderer
                widget={activeDetail.widget}
                searchData={searchData}
              />
            </DashboardDetailPanel>
          )}
        </div>

        {/* Hidden widget library: uses current state, so hide/add is reflected without another search. */}
        {isCatalogOpen && (
          <div className="absolute bottom-0 right-0 top-0 z-20 w-72 border-l border-gray-200 bg-white shadow-lg">
            <div className="flex items-start justify-between border-b border-gray-100 px-4 py-3">
              <div>
                <div className="text-base font-bold text-slate-900">
                  위젯 추가
                </div>
                <div className="mt-1 text-xs text-slate-500">
                  숨김 위젯 {availableWidgets.length}
                </div>
              </div>
              <button
                type="button"
                className="h-7 rounded px-2 text-xs text-slate-500 hover:bg-slate-100 hover:text-slate-900"
                onClick={() => setIsCatalogOpen(false)}
              >
                Close
              </button>
            </div>

            <div className="flex h-[calc(100%-61px)] flex-col gap-2 overflow-y-auto p-3">
              {availableWidgets.map((widget) => {
                const previewIcon = widgetPreviewIcons[widget.nm_file];
                const previewText = widget.dc_widget || widget.nm_file;

                return (
                  <button
                    key={widget.cd_widget}
                    type="button"
                    className="w-full rounded-md border border-gray-200 bg-white p-3 text-left shadow-sm transition hover:border-blue-300 hover:bg-blue-50 hover:shadow"
                    onClick={() => addWidget(widget)}
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-slate-50">
                        {previewIcon ? (
                          <img
                            src={previewIcon}
                            alt=""
                            className="h-10 w-10 object-contain"
                          />
                        ) : (
                          <span className="text-xs font-semibold text-slate-400">
                            {widget.nm_widget?.slice(0, 1) || "W"}
                          </span>
                        )}
                      </div>

                      <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <div className="truncate text-sm font-semibold text-slate-900">
                              {widget.nm_widget}
                            </div>
                            <div className="mt-1 truncate text-xs text-slate-500">
                              {previewText}
                            </div>
                          </div>
                          <span className="shrink-0 rounded bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">
                            {widget.qt_w_default || DEFAULT_WIDGET_W} x{" "}
                            {widget.qt_h_default || DEFAULT_WIDGET_H}
                          </span>
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}

              {availableWidgets.length === 0 && (
                <div className="rounded-md border border-dashed border-gray-200 bg-gray-50 px-3 py-6 text-center text-xs text-slate-500">
                  추가 가능한 위젯이 없습니다.
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

function WidgetFrame({
  widget,
  children,
  onHide,
  onOpenDetail,
}: {
  widget: DashboardWidget;
  children: React.ReactNode;
  onHide: (cdWidget: string) => void;
  onOpenDetail: (widget: DashboardWidget) => void;
}) {
  return (
    <div className="dashboard-widget-drag-handle group relative h-full w-full cursor-move overflow-hidden rounded-lg border border-gray-200 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.06),0_8px_24px_rgba(15,23,42,0.06)] transition-shadow duration-150 hover:shadow-[0_2px_6px_rgba(15,23,42,0.08),0_14px_32px_rgba(15,23,42,0.10)]">
      <button
        type="button"
        className="absolute right-2 top-2 z-10 h-6 rounded border border-gray-200 bg-white/90 px-2 text-xs text-gray-500 opacity-0 shadow-sm transition-opacity hover:bg-gray-50 hover:text-gray-700 group-hover:opacity-100 group-focus-within:opacity-100"
        onMouseDown={(event) => event.stopPropagation()}
        onClick={(event) => {
          event.stopPropagation();
          onHide(widget.cd_widget);
        }}
      >
        Hide
      </button>
      <div className="flex h-full w-full flex-col overflow-hidden">
        <div className="min-h-0 w-full flex-1 overflow-hidden">{children}</div>
        {/* 상세보기는 위젯 파일이 아니라 sys_mwidget.yn_detail로 켠다. */}
        {widget.yn_detail === "Y" && (
          <div className="flex shrink-0 justify-end px-4 pb-2">
            <button
              type="button"
              className="rounded px-1 text-sm text-slate-700 hover:text-blue-700"
              onMouseDown={(event) => event.stopPropagation()}
              onClick={(event) => {
                event.stopPropagation();
                onOpenDetail(widget);
              }}
            >
              상세보기&gt;&gt;
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function WidgetRenderer({
  widget,
  searchData,
  refreshKey,
}: {
  widget: DashboardWidget;
  searchData: any;
  refreshKey: number;
}) {
  const WidgetComponent = widgetMap[widget.nm_file];

  if (!WidgetComponent) {
    return (
      <div className="flex h-full w-full items-center justify-center text-xs text-red-500">
        Widget file not found: {widget.nm_file}
      </div>
    );
  }

  return (
    <WidgetComponent
      cdWidget={widget.cd_widget}
      nmWidget={widget.nm_widget}
      dashboardWidget={widget}
      searchData={searchData}
      refreshKey={refreshKey}
    />
  );
}

// 상세 팝업 본문. 위젯 파일명 + "_01" 파일을 찾아 띄운다. (예: sn_sys_widget_011 → sn_sys_widget_011_01)
function WidgetDetailRenderer({
  widget,
  searchData,
}: {
  widget: DashboardWidget;
  searchData: any;
}) {
  const detailFile = `${widget.nm_file}_01`;
  const DetailComponent = widgetMap[detailFile];

  if (!DetailComponent) {
    return (
      <div className="flex h-full w-full items-center justify-center text-xs text-red-500">
        Detail file not found: {detailFile}
      </div>
    );
  }

  return (
    <DetailComponent
      cdWidget={widget.cd_widget}
      nmWidget={widget.nm_widget}
      searchData={searchData}
    />
  );
}

export default SN_SYS_DSH_REP_001;
